import {
    createAsyncThunk,
    createSlice,
} from '@reduxjs/toolkit';

import {
    add,
    deleteRecord,
    fetch,
    fetchRecords,
    update,
} from '../services/ApplicationsService.ts';


import {
    deleteOneCase,
    fetchManyCase,
    fetchOneCase,
    saveOneCase,
    updateOneCase,
} from '../../../../store/RecordsSliceCaseHelpers';
import {Application} from '../types';

const entityKey = 'applications';

interface ApplicationsState {
    byId: { [key: string]: Application },
    ids: string[],
    totalCount: number,
    loading: boolean,
    error: string
}

const initialState = {
    byId: {},
    ids: [],
    totalCount: 0,
    loading: false,
    error: '',
} as ApplicationsState;


const normalizeRecord = (record: Application) => {
    return Object.assign({}, record);
};

// FNP-783: the enable-list is business-apps-only — never ADMIN consoles. The
// ApplicationCategory enum is ADMIN | BUSINESS, so filtering to BUSINESS excludes
// admin apps at the server. (Defense-in-depth; the security boundary is the
// server-side authz on createAccountApplication — FNP-782.)
const BUSINESS_APPLICATION_CATEGORY = 'BUSINESS';

// generates pending, fulfilled and rejected
export const fetchApplications = createAsyncThunk(
    `${entityKey}/fetchRecords`,
    () => {

        return fetchRecords({applicationCategory: BUSINESS_APPLICATION_CATEGORY})
            .then((response) => {
                if (response) {
                    // FNP-783 belt-and-suspenders: never surface an ADMIN app in
                    // the enable-list even if the server filter is bypassed/changed.
                    return response
                        .filter((record: Application) => record.applicationCategory !== 'ADMIN')
                        .map((record: Application) => {
                            return normalizeRecord(record);
                        });
                }
            });
    },
);

export const fetchApplication = createAsyncThunk(
    `${entityKey}/fetch`,
    (id: string) => {
        return fetch(id)
            .then((response) => {
                if (typeof response !== 'undefined') {
                    return normalizeRecord(response);
                }
            });
    });

export const saveApplication = createAsyncThunk(
    `${entityKey}/add`,
    (payload: Application) => {
        return add(payload)
            .then((response) => {
                if (response) {
                    return normalizeRecord(response);
                }
            });
    });

export const updateApplication = createAsyncThunk(
    `${entityKey}/update`,
    ({
         id,
         record,
     }: { id: string, record: Application }) => {

        return update(id, record)
            .then((response) => {
                if (response) {
                    return normalizeRecord(response);
                }
            });
    },
);

export const deleteApplication = createAsyncThunk(
    `${entityKey}/deleteRecord`,
    (id: string) => {
        return deleteRecord(id)
            .then(() => id);
    },
);


export const storeApplication = createAsyncThunk(
    `${entityKey}/store`,
    (payload: Application) => {
        return normalizeRecord(payload);
    },
);


const componentsSlice = createSlice({
                                        name: entityKey,
                                        initialState,
                                        reducers: {
                                            clearState: () => initialState,
                                        },
                                        extraReducers: (builder) => {
                                            // FETCH MANY
                                            fetchManyCase(builder, fetchApplications, entityKey);

                                            // FETCH ONE
                                            fetchOneCase(builder, fetchApplication);

                                            // SAVE ONE
                                            saveOneCase(builder, saveApplication);

                                            // UPDATE ONE
                                            updateOneCase(builder, updateApplication);

                                            updateOneCase(builder, storeApplication);

                                            // DELETE ONE
                                            deleteOneCase(builder, deleteApplication);
                                        },
                                    });

export default componentsSlice.reducer;
